import { assign, setup, type AnyActorRef } from 'xstate';
import { DateTime } from 'luxon';
import { TripLegType } from '@/data/models/reservation.model';
import type { TripArrayFieldContext } from './createTripArrayFieldMachine';
import type { TripTypeContext } from './tripType.machine';
import type { WillCallContext } from './willCall.machine';
import type { StandingOrderContext } from './standingOrder.machine';
import {
  levelOfCareMachine,
  type LevelOfCareContext,
} from './levelOfCare.machine';
import { passengerMobilityMachine } from './passengerMobility.machine';
import { passengerAssistanceMachine } from './passengerAssistance.machine';
import { standingOrderMachine } from './standingOrder.machine';
import { appointmentDateMachine } from './appointmentDate.machine';
import { appointmentDateTimeMachine } from './appointmentDateTime.machine';
import { dropoffAddressMachine } from './dropoffAddress.machine';
import { dropoffNotesMachine } from './dropoffNotes.machine';
import { dropoffPhoneMachine } from './dropoffPhone.machine';
import { pickupAddressMachine } from './pickupAddress.machine';
import { pickupDateMachine } from './pickupDate.machine';
import { pickupDateTimeMachine } from './pickupDateTime.machine';
import { pickupNotesMachine } from './pickupNotes.machine';
import { pickupPhoneMachine } from './pickupPhone.machine';
import { reasonForTripMachine } from './reasonForTrip.machine';
import { tripTypeMachine } from './tripType.machine';
import { willCallMachine } from './willCall.machine';
import { tripLegAddressConfidenceMachine } from './tripLegAddressConfidence.machine';
import {
  reservationFormSchema,
  type ReservationFormValues,
  type TripSegmentValues,
} from '../schema/reservationForm.schema';

export type ReservationFormMainContext = {
  tripTypeRef: AnyActorRef | null;
  reasonForTripRef: AnyActorRef | null;
  appointmentDateRef: AnyActorRef | null;
  appointmentDateTimeRef: AnyActorRef | null;
  pickupDateRef: AnyActorRef | null;
  pickupDateTimeRef: AnyActorRef | null;
  pickupAddressRef: AnyActorRef | null;
  dropoffAddressRef: AnyActorRef | null;
  pickupPhoneRef: AnyActorRef | null;
  dropoffPhoneRef: AnyActorRef | null;
  pickupNotesRef: AnyActorRef | null;
  dropoffNotesRef: AnyActorRef | null;
  willCallRef: AnyActorRef | null;
  standingOrderRef: AnyActorRef | null;
  levelOfCareRef: AnyActorRef | null;
  passengerMobilityRef: AnyActorRef | null;
  passengerAssistanceRef: AnyActorRef | null;
  tripLegAddressConfidenceRef: AnyActorRef | null;
};

export type ReservationFormMainEvent =
  | { type: 'SUBMIT' }
  | { type: 'SUBMIT_SUCCESS' }
  | { type: 'SUBMIT_ERROR' }
  | { type: 'VALIDATION_SUCCESS' }
  | { type: 'VALIDATION_ERROR' }
  | { type: 'RESET_ALL' }
  | { type: 'SET_TRIP_COUNT'; count: number };

function readTripArrayField(ref: AnyActorRef | null): unknown[] {
  if (!ref) return [''];
  const ctx = ref.getSnapshot().context as TripArrayFieldContext;
  return ctx.values.length ? ctx.values : [''];
}

function readTripTypeField(ref: AnyActorRef | null): TripLegType {
  if (!ref) return TripLegType.OneWay;
  const ctx = ref.getSnapshot().context as TripTypeContext;
  return ctx.value;
}

function readWillCallArray(ref: AnyActorRef | null): boolean[] {
  if (!ref) return [false];
  const ctx = ref.getSnapshot().context as WillCallContext;
  return ctx.values.length ? ctx.values : [false];
}

function readStandingOrderArray(ref: AnyActorRef | null): boolean[] {
  if (!ref) return [false];
  const ctx = ref.getSnapshot().context as StandingOrderContext;
  return ctx.values.length ? ctx.values : [false];
}

function readLevelOfCareStringArray(
  ref: AnyActorRef | null,
  key:
    | 'attributeLevel1Values'
    | 'attributeLevel2Values'
    | 'attributeLevel3Values'
    | 'attributeLevel4Values'
    | 'attributeLevel5Values'
    | 'attributeLevel6Values',
): string[] {
  if (!ref) return [''];
  const ctx = ref.getSnapshot().context as LevelOfCareContext;
  const arr = ctx[key];
  return arr.length ? arr : [''];
}

function getTripsFromMachines(context: ReservationFormMainContext): TripSegmentValues[] {
  const reasonForTrips = readTripArrayField(context.reasonForTripRef) as string[];
  const appointmentDateTimeIso = readTripArrayField(
    context.appointmentDateTimeRef,
  ) as string[];
  const appointmentDates = readTripArrayField(context.appointmentDateRef) as string[];
  const pickupDateTimeIso = readTripArrayField(context.pickupDateTimeRef) as string[];
  const pickupDates = readTripArrayField(context.pickupDateRef) as string[];
  const pickupAddresses = readTripArrayField(context.pickupAddressRef);
  const dropoffAddresses = readTripArrayField(context.dropoffAddressRef);
  const pickupPhones = readTripArrayField(context.pickupPhoneRef) as string[];
  const dropoffPhones = readTripArrayField(context.dropoffPhoneRef) as string[];
  const pickupNotes = readTripArrayField(context.pickupNotesRef) as string[];
  const dropoffNotes = readTripArrayField(context.dropoffNotesRef) as string[];
  const willCalls = readWillCallArray(context.willCallRef);
  const standingOrders = readStandingOrderArray(context.standingOrderRef);
  const passengerMobilities = readTripArrayField(
    context.passengerMobilityRef,
  ) as string[];
  const passengerAssistances = readTripArrayField(
    context.passengerAssistanceRef,
  ) as string[];
  const attributeLevel1Values = readLevelOfCareStringArray(
    context.levelOfCareRef,
    'attributeLevel1Values',
  );
  const attributeLevel2Values = readLevelOfCareStringArray(
    context.levelOfCareRef,
    'attributeLevel2Values',
  );
  const attributeLevel3Values = readLevelOfCareStringArray(
    context.levelOfCareRef,
    'attributeLevel3Values',
  );
  const attributeLevel4Values = readLevelOfCareStringArray(
    context.levelOfCareRef,
    'attributeLevel4Values',
  );
  const attributeLevel5Values = readLevelOfCareStringArray(
    context.levelOfCareRef,
    'attributeLevel5Values',
  );
  const attributeLevel6Values = readLevelOfCareStringArray(
    context.levelOfCareRef,
    'attributeLevel6Values',
  );
  const len = Math.max(
    reasonForTrips.length,
    appointmentDateTimeIso.length,
    appointmentDates.length,
    pickupDateTimeIso.length,
    pickupDates.length,
    pickupAddresses.length,
    dropoffAddresses.length,
    pickupPhones.length,
    dropoffPhones.length,
    pickupNotes.length,
    dropoffNotes.length,
    willCalls.length,
    standingOrders.length,
    passengerMobilities.length,
    passengerAssistances.length,
    attributeLevel1Values.length,
    attributeLevel2Values.length,
    attributeLevel3Values.length,
    attributeLevel4Values.length,
    attributeLevel5Values.length,
    attributeLevel6Values.length,
    1,
  );
  const trips: TripSegmentValues[] = [];
  for (let i = 0; i < len; i++) {
    const iso = appointmentDateTimeIso[i] ?? '';
    const parsed = iso ? DateTime.fromISO(iso) : null;
    const pickupIso = pickupDateTimeIso[i] ?? '';
    const pickupParsed = pickupIso ? DateTime.fromISO(pickupIso) : null;
    const isWillCall = willCalls[i] ?? false;
    const appointmentDateTime = isWillCall
      ? (null as unknown as TripSegmentValues['appointmentDateTime'])
      : parsed?.isValid === true
        ? parsed
        : (null as unknown as TripSegmentValues['appointmentDateTime']);
    const pickupDateTime = isWillCall
      ? (null as unknown as TripSegmentValues['pickupDateTime'])
      : pickupParsed?.isValid === true
        ? pickupParsed
        : (null as unknown as TripSegmentValues['pickupDateTime']);
    trips.push({
      reasonForTrip: reasonForTrips[i] ?? '',
      appointmentDate: appointmentDates[i] ?? '',
      isWillCall,
      appointmentDateTime,
      pickupDate: pickupDates[i] ?? '',
      pickupDateTime,
      pickupAddress: (pickupAddresses[i] ?? '') as TripSegmentValues['pickupAddress'],
      dropoffAddress: (dropoffAddresses[i] ?? '') as TripSegmentValues['dropoffAddress'],
      pickupPhone: pickupPhones[i] ?? '',
      dropoffPhone: dropoffPhones[i] ?? '',
      pickupPhoneExtension: '',
      dropoffPhoneExtension: '',
      pickupNotes: pickupNotes[i] ?? '',
      dropoffNotes: dropoffNotes[i] ?? '',
      roundTripWillCall: true,
      standingOrder: standingOrders[i] ?? false,
      passengerMobility: passengerMobilities[i] ?? '',
      passengerAssistance: passengerAssistances[i] ?? '',
      AttributeLevel1: attributeLevel1Values[i] ?? '',
      AttributeLevel2: attributeLevel2Values[i] ?? '',
      AttributeLevel3: attributeLevel3Values[i] ?? '',
      AttributeLevel4: attributeLevel4Values[i] ?? '',
      AttributeLevel5: attributeLevel5Values[i] ?? '',
      AttributeLevel6: attributeLevel6Values[i] ?? '',
      MobilityAttribute: passengerAssistances[i] ?? '',
      height: '',
      weight: '',
    });
  }
  return trips;
}

export function getAggregatedFormValues(
  context: ReservationFormMainContext,
): ReservationFormValues {
  return {
    tripType: readTripTypeField(context.tripTypeRef),
    trips: getTripsFromMachines(context),
  };
}

const initialContext: ReservationFormMainContext = {
  tripTypeRef: null,
  reasonForTripRef: null,
  appointmentDateRef: null,
  appointmentDateTimeRef: null,
  pickupDateRef: null,
  pickupDateTimeRef: null,
  pickupAddressRef: null,
  dropoffAddressRef: null,
  pickupPhoneRef: null,
  dropoffPhoneRef: null,
  pickupNotesRef: null,
  dropoffNotesRef: null,
  willCallRef: null,
  standingOrderRef: null,
  levelOfCareRef: null,
  passengerMobilityRef: null,
  passengerAssistanceRef: null,
  tripLegAddressConfidenceRef: null,
};

function canSubmitFromContext(context: ReservationFormMainContext): boolean {
  const aggregated = getAggregatedFormValues(context);
  const parsed = reservationFormSchema.safeParse(aggregated);
  return parsed.success;
}

function isTripAddressFilled(
  address: TripSegmentValues['pickupAddress'] | TripSegmentValues['dropoffAddress'],
): boolean {
  if (typeof address === 'string') {
    return address.trim().length > 0;
  }
  if (address && typeof address === 'object') {
    if (address.isHeader === true || address.place_id === 'header') {
      return false;
    }
    return Boolean(
      address.place_id?.trim() ||
        address.mainText?.trim() ||
        address.description?.trim(),
    );
  }
  return false;
}

function tripLegMeetsDraftRequirements(trip: TripSegmentValues): boolean {
  if (!String(trip.reasonForTrip ?? '').trim()) {
    return false;
  }

  if (trip.isWillCall) {
    if (!String(trip.appointmentDate ?? '').trim()) {
      return false;
    }
  } else {
    const appt = trip.appointmentDateTime;
    if (
      appt == null ||
      !(appt instanceof DateTime && appt.isValid)
    ) {
      return false;
    }
  }

  if (!trip.isWillCall) {
    const pickup = trip.pickupDateTime;
    if (
      pickup == null ||
      !(pickup instanceof DateTime && pickup.isValid)
    ) {
      return false;
    }
  }

  if (!isTripAddressFilled(trip.pickupAddress)) {
    return false;
  }
  if (!isTripAddressFilled(trip.dropoffAddress)) {
    return false;
  }
  if (!String(trip.AttributeLevel1 ?? '').trim()) {
    return false;
  }
  if (!String(trip.MobilityAttribute ?? '').trim()) {
    return false;
  }

  return true;
}

/** True when every trip leg has purpose, appointment, pickup time (or will-call), addresses, AttributeLevel1, and MobilityAttribute. */
export function canSaveAsDraftFromContext(
  context: ReservationFormMainContext,
): boolean {
  const trips = getTripsFromMachines(context);
  if (trips.length === 0) {
    return false;
  }
  return trips.every(tripLegMeetsDraftRequirements);
}

export const reservationFormMainMachine = setup({
  types: {
    context: {} as ReservationFormMainContext,
    events: {} as ReservationFormMainEvent,
  },
  actors: {
    tripType: tripTypeMachine,
    reasonForTrip: reasonForTripMachine,
    appointmentDate: appointmentDateMachine,
    appointmentDateTime: appointmentDateTimeMachine,
    pickupDate: pickupDateMachine,
    pickupDateTime: pickupDateTimeMachine,
    pickupAddress: pickupAddressMachine,
    dropoffAddress: dropoffAddressMachine,
    pickupPhone: pickupPhoneMachine,
    dropoffPhone: dropoffPhoneMachine,
    pickupNotes: pickupNotesMachine,
    dropoffNotes: dropoffNotesMachine,
    willCall: willCallMachine,
    standingOrder: standingOrderMachine,
    levelOfCare: levelOfCareMachine,
    passengerMobility: passengerMobilityMachine,
    passengerAssistance: passengerAssistanceMachine,
    tripLegAddressConfidence: tripLegAddressConfidenceMachine,
  },
  actions: {
    syncTripCount: ({ context, event }: { context: ReservationFormMainContext; event: ReservationFormMainEvent }) => {
      if (event.type !== 'SET_TRIP_COUNT') return;
      const count = Math.max(1, event.count);
      const refs: (AnyActorRef | null)[] = [
        context.reasonForTripRef,
        context.appointmentDateRef,
        context.appointmentDateTimeRef,
        context.pickupDateRef,
        context.pickupDateTimeRef,
        context.pickupAddressRef,
        context.dropoffAddressRef,
        context.pickupPhoneRef,
        context.dropoffPhoneRef,
        context.pickupNotesRef,
        context.dropoffNotesRef,
        context.willCallRef,
        context.standingOrderRef,
        context.levelOfCareRef,
        context.passengerMobilityRef,
        context.passengerAssistanceRef,
      ];
      refs.forEach((r) => r?.send({ type: 'RESIZE', count }));
    },
    resetAllFields: ({ context }: { context: ReservationFormMainContext }) => {
      context.tripTypeRef?.send({ type: 'RESET' });
      context.reasonForTripRef?.send({ type: 'RESET' });
      const tripFieldRefs: (AnyActorRef | null)[] = [
        context.reasonForTripRef,
        context.appointmentDateRef,
        context.appointmentDateTimeRef,
        context.pickupDateRef,
        context.pickupDateTimeRef,
        context.pickupAddressRef,
        context.dropoffAddressRef,
        context.pickupPhoneRef,
        context.dropoffPhoneRef,
        context.pickupNotesRef,
        context.dropoffNotesRef,
        context.willCallRef,
        context.standingOrderRef,
        context.passengerMobilityRef,
        context.passengerAssistanceRef,
      ];
      tripFieldRefs.forEach((r) => r?.send({ type: 'RESET' }));
      context.levelOfCareRef?.send({ type: 'RESET' });
      context.tripLegAddressConfidenceRef?.send({ type: 'RESET' });
    },
  },
  guards: {
    canSubmit: ({ context }) => canSubmitFromContext(context),
    canSaveAsDraft: ({ context }) => canSaveAsDraftFromContext(context),
  },
}).createMachine({
  id: 'reservationFormMain',
  context: initialContext,
  initial: 'bootstrap',
  states: {
    bootstrap: {
      entry: assign(({ spawn }) => {
        const pickupAddressRef = spawn('pickupAddress', {
          id: 'pickupAddress',
        });
        const dropoffAddressRef = spawn('dropoffAddress', {
          id: 'dropoffAddress',
        });
        return {
          tripTypeRef: spawn('tripType', { id: 'tripType' }),
          reasonForTripRef: spawn('reasonForTrip', { id: 'reasonForTrip' }),
          appointmentDateRef: spawn('appointmentDate', {
            id: 'appointmentDate',
          }),
          appointmentDateTimeRef: spawn('appointmentDateTime', {
            id: 'appointmentDateTime',
          }),
          pickupDateRef: spawn('pickupDate', { id: 'pickupDate' }),
          pickupDateTimeRef: spawn('pickupDateTime', { id: 'pickupDateTime' }),
          pickupAddressRef,
          dropoffAddressRef,
          tripLegAddressConfidenceRef: spawn('tripLegAddressConfidence', {
            id: 'tripLegAddressConfidence',
            input: { pickupAddressRef, dropoffAddressRef },
          }),
          pickupPhoneRef: spawn('pickupPhone', { id: 'pickupPhone' }),
          dropoffPhoneRef: spawn('dropoffPhone', { id: 'dropoffPhone' }),
          pickupNotesRef: spawn('pickupNotes', { id: 'pickupNotes' }),
          dropoffNotesRef: spawn('dropoffNotes', { id: 'dropoffNotes' }),
          willCallRef: spawn('willCall', { id: 'willCall' }),
          standingOrderRef: spawn('standingOrder', { id: 'standingOrder' }),
          levelOfCareRef: spawn('levelOfCare', { id: 'levelOfCare' }),
          passengerMobilityRef: spawn('passengerMobility', {
            id: 'passengerMobility',
          }),
          passengerAssistanceRef: spawn('passengerAssistance', {
            id: 'passengerAssistance',
          }),
        };
      }),
      always: { target: 'editing' },
    },
    editing: {
      on: {
        SUBMIT: {
          target: 'submitting',
          guard: 'canSubmit',
        },
        RESET_ALL: { actions: 'resetAllFields' },
      },
    },
    validating: {
      on: {
        VALIDATION_SUCCESS: { target: 'editing' },
        VALIDATION_ERROR: { target: 'editing' },
      },
    },
    submitting: {
      on: {
        SUBMIT_SUCCESS: 'success',
        SUBMIT_ERROR: { target: 'editing' },
      },
    },
    success: {
      on: {
        RESET_ALL: {
          target: 'editing',
          actions: 'resetAllFields',
        },
      },
    },
  },
  on: {
    SET_TRIP_COUNT: { actions: 'syncTripCount' },
  },
});
